import { useState, useCallback } from 'react';
import { useApiClient } from './useApiClient';

export interface CityValidationResult {
  valid: boolean;
  error?: string;
  matched_location?: any;
  canonical_name?: string;
  location_id?: string;
  country_code?: string;
  target_type?: string;
  confidence?: number;
  suggestions?: Array<{
    name: string;
    canonical_name: string;
    location_id: string;
    country_code: string;
    target_type: string;
  }>;
}

export interface ValidationSummary {
  total_cities: number;
  valid_cities: number;
  invalid_cities: number;
  validation_rate: number;
}

export interface CityValidationResponse {
  validation_results: Record<string, CityValidationResult>;
  summary: ValidationSummary;
}

export const useCityValidation = () => {
  const { callApi } = useApiClient();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState<Record<string, CityValidationResult> | null>(null);
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateCities = useCallback(async (cities: string[]) => {
    if (cities.length === 0) {
      setValidationResults({});
      setValidationSummary({
        total_cities: 0,
        valid_cities: 0,
        invalid_cities: 0,
        validation_rate: 0
      });
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await callApi<CityValidationResponse>('/api/v1/serp/validate-cities', {
        method: 'POST',
        body: JSON.stringify({ cities }),
      });

      if (response) {
        setValidationResults(response.validation_results);
        setValidationSummary(response.summary);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to validate cities');
      setValidationResults(null);
      setValidationSummary(null);
    } finally {
      setIsValidating(false);
    }
  }, [callApi]);

  const clearValidation = useCallback(() => {
    setValidationResults(null);
    setValidationSummary(null);
    setError(null);
  }, []);

  return {
    validateCities,
    clearValidation,
    isValidating,
    validationResults,
    validationSummary,
    error
  };
}; 